/**
 * @file: useFacadeSiteRequirements.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  facadeSiteHasMedia,
  facadeSiteRequirementsOf,
  resolveFacadeSites,
  uncheckedRequiredFacadeSites,
} from '@euler/functions/inventory/facadeSiteRequirements';
import { InspectionSiteInfo } from '@euler/model';
import { usePersistFn } from '@euler/utils/hooks';
import { useMemo } from 'react';
import { findFacadeInspectionFlow } from './useFacadeInspectionFlow';

/**
 * Exposes the per-site requirements configured on the facade inspection
 * template the current pre-inspection was started with.
 */
export const useFacadeSiteRequirements = () => {
  const {
    detail,
    taskManager,
    inventoryQuery: { getSiteById },
  } = useTaskContext();

  // deliberately resolved off the task detail alone: this hook is also mounted
  // by the task detail page, which must not suspend on a templates fetch.
  const facadeInspectionFlow = useMemo(
    () => findFacadeInspectionFlow(detail.preInspection.flows),
    [detail.preInspection.flows],
  );

  const { requiredSiteIds, mediaCaptureRequiredSiteIds } = useMemo(
    () => facadeSiteRequirementsOf(facadeInspectionFlow?.template.conf),
    [facadeInspectionFlow],
  );

  /** Every required facade site, in the order the template declares them. */
  const requiredSites = useMemo(
    () => resolveFacadeSites(requiredSiteIds, getSiteById),
    [getSiteById, requiredSiteIds],
  );

  /**
   * The required facade sites that have not been recorded with any photo yet,
   * in the order the template declares them.
   */
  const getUncheckedRequiredSites = usePersistFn((): InspectionSiteInfo[] => {
    if (!requiredSiteIds.length) return [];

    const flowId = facadeInspectionFlow?.id;
    const committed = new Map(
      detail.preInspection.sites
        .filter(x => x.flowId == null || x.flowId === flowId)
        .map(x => [x.siteId, x] as const),
    );
    // the staging area leads the committed detail - a photo that finished
    // uploading is only committed a beat later, and the commit itself can fail.
    // it is empty outside the pre-inspection screen, where committed is all
    // there is.
    const staged = taskManager.preInspectionManager.siteInspections$.getValue();

    return uncheckedRequiredFacadeSites(
      requiredSiteIds,
      siteId =>
        facadeSiteHasMedia(staged.get(siteId)) ||
        facadeSiteHasMedia(committed.get(siteId)),
      getSiteById,
    );
  });

  /** Whether the site's media must be captured live instead of picked. */
  const isMediaCaptureRequired = usePersistFn((siteId: number) =>
    mediaCaptureRequiredSiteIds.has(siteId),
  );

  return { requiredSites, getUncheckedRequiredSites, isMediaCaptureRequired };
};
